import styled from 'styled-components'

export const BillingContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`

export const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1rem 0;
  border-bottom: 1px solid #eee;
`

export const Title = styled.h1`
  color: #333;
  margin: 0;
`

export const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  color: #666;
  text-decoration: none;

  &:hover {
    background: #f8f9fa;
    color: #333;
  }
`

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
  font-size: 1.1rem;
  color: #666;
`

export const ErrorContainer = styled.div`
  padding: 2rem;
  background: #fee;
  border: 1px solid #fcc;
  border-radius: 8px;
  color: #c33;
  text-align: center;
`

export const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`

export const Card = styled.div<{ $featured?: boolean }>`
  background: white;
  border: 1px solid ${props => (props.$featured ? '#007bff' : '#e9ecef')};
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;

  ${props =>
    props.$featured &&
    `
    border-width: 2px;
    
    &::before {
      content: 'Current Plan';
      position: absolute;
      top: -10px;
      left: 50%;
      transform: translateX(-50%);
      background: #007bff;
      color: white;
      padding: 0.25rem 1rem;
      border-radius: 20px;
      font-size: 0.8rem;
      font-weight: 600;
    }
  `}
`

export const CardHeader = styled.div`
  margin-bottom: 1rem;
`

export const CardTitle = styled.h2`
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 1.25rem;
`

export const CardSubtitle = styled.p`
  margin: 0;
  color: #666;
  font-size: 0.9rem;
`

export const PlanPrice = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: 1rem;
`

export const PriceAmount = styled.span`
  font-size: 2rem;
  font-weight: 700;
  color: #333;
`

export const PricePeriod = styled.span`
  font-size: 1rem;
  color: #666;
  margin-left: 0.25rem;
`

export const StatusBadge = styled.span<{
  $status: 'active' | 'canceled' | 'past_due' | 'incomplete' | 'trialing'
}>`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;

  ${props => {
    switch (props.$status) {
      case 'active':
        return `
          background: #d4edda;
          color: #155724;
        `
      case 'trialing':
        return `
          background: #d1ecf1;
          color: #0c5460;
        `
      case 'past_due':
        return `
          background: #f8d7da;
          color: #721c24;
        `
      case 'canceled':
        return `
          background: #f3f4f6;
          color: #6b7280;
        `
      default:
        return `
          background: #fff3cd;
          color: #856404;
        `
    }
  }}
`

export const UsageSection = styled.div`
  margin-bottom: 1.5rem;
`

export const UsageItem = styled.div`
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
`

export const UsageLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: #666;
`

export const UsageValue = styled.span<{ $overLimit?: boolean }>`
  font-weight: 600;
  color: ${props => (props.$overLimit ? '#dc3545' : '#333')};
`

export const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
`

export const ProgressFill = styled.div<{
  $percentage: number
  $overLimit?: boolean
}>`
  height: 100%;
  width: ${props => Math.min(props.$percentage, 100)}%;
  background: ${props =>
    props.$overLimit
      ? '#dc3545'
      : props.$percentage > 80
      ? '#fd7e14'
      : '#28a745'};
  transition: width 0.3s ease;
`

export const RenewalInfo = styled.div`
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #666;
`

export const OverageSection = styled.div`
  padding: 1rem;
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  margin-bottom: 1rem;
`

export const OverageTitle = styled.h4`
  margin: 0 0 0.5rem 0;
  color: #856404;
  font-size: 0.9rem;
`

export const OverageItem = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: #856404;
  margin-bottom: 0.25rem;

  &:last-child {
    margin-bottom: 0;
    font-weight: 600;
    border-top: 1px solid #ffeaa7;
    padding-top: 0.5rem;
    margin-top: 0.5rem;
  }
`

export const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

export const Button = styled.button<{
  $variant?: 'primary' | 'secondary' | 'danger' | 'success'
  $fullWidth?: boolean
}>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  transition: all 0.2s ease;
  width: ${props => (props.$fullWidth ? '100%' : 'auto')};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: #007bff;
          color: white;
          
          &:hover:not(:disabled) {
            background: #0056b3;
          }
        `
      case 'success':
        return `
          background: #28a745;
          color: white;
          
          &:hover:not(:disabled) {
            background: #1e7e34;
          }
        `
      case 'danger':
        return `
          background: #dc3545;
          color: white;
          
          &:hover:not(:disabled) {
            background: #c82333;
          }
        `
      case 'secondary':
      default:
        return `
          background: #6c757d;
          color: white;
          
          &:hover:not(:disabled) {
            background: #545b62;
          }
        `
    }
  }}
`

export const PlanComparisonSection = styled.div`
  margin-top: 3rem;
  padding: 2rem;
  background: #f8f9fa;
  border-radius: 12px;
`

export const SectionTitle = styled.h2`
  margin: 0 0 1.5rem 0;
  color: #333;
  text-align: center;
`

export const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
`

export const PlanCard = styled.div<{
  $current?: boolean
  $recommended?: boolean
}>`
  background: white;
  border: 2px solid
    ${props =>
      props.$current ? '#007bff' : props.$recommended ? '#28a745' : '#e9ecef'};
  border-radius: 12px;
  padding: 1.5rem;
  position: relative;
  text-align: center;

  ${props =>
    props.$recommended &&
    `
    transform: scale(1.05);
    
    &::before {
      content: 'Recommended';
      position: absolute;
      top: -12px;
      left: 50%;
      transform: translateX(-50%);
      background: #28a745;
      color: white;
      padding: 0.25rem 1rem;
      border-radius: 20px;
      font-size: 0.8rem;
      font-weight: 600;
    }
  `}

  ${props =>
    props.$current &&
    `
    &::after {
      content: 'Current Plan';
      position: absolute;
      top: -12px;
      left: 50%;
      transform: translateX(-50%);
      background: #007bff;
      color: white;
      padding: 0.25rem 1rem;
      border-radius: 20px;
      font-size: 0.8rem;
      font-weight: 600;
    }
  `}
`

export const PlanName = styled.h3`
  margin: 0 0 0.5rem 0;
  color: #333;
`

export const PlanDescription = styled.p`
  margin: 0 0 1rem 0;
  color: #666;
  font-size: 0.9rem;
`

export const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
  text-align: left;
`

export const Feature = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: #666;

  &::before {
    content: '✓';
    color: #28a745;
    font-weight: bold;
    margin-right: 0.5rem;
  }
`
